import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  memberSkillSchema,
  type MemberSkill,
  identityRowSchema,
  metafieldSchema,
  personSchema,
  personScheduleSchema,
  rateAssignmentListSchema,
  projectSchema,
  taskFacetsSchema,
  toWireDate,
  worklogFacetsSchema,
  worklogSchema,
  type IdentityRow,
  type Metafield,
  type Person,
  type Team,
  type PersonSchedule,
  type RateAssignment,
  type Project,
  type TaskFacets,
  type Worklog,
  type WorklogFacets,
} from '@forecastify/shared';
import { useSession } from '../../session';
import { failureOf, type ScreenFailure } from '../screen-state';
import { parseList } from '../permissions-hook';
import { useTeams } from '../use-teams';
import { readAll, requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ EVERYTHING ONE PERSON'S DETAIL NEEDS — item 46.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EXTRACTED SO THE LIST CAN OPEN A PERSON WITHOUT NAVIGATING.** Item 46: *"person gains
 * modal and drawer — fullscreen is the only view today"*. A person was reachable only as a route,
 * so every look at one cost the list — its page, its filters and its scroll position — and coming
 * back re-fetched all of it. The list opens a modal over itself now, and the route stays for a
 * deep link.
 *
 * ⚠️ **The alternative was a second copy of these nine requests in the list route**, which is the
 * shape that drifts: the copy nobody opens is the one that stops asking for a field the screen
 * started drawing.
 *
 * ⚠️ **`null` id means load nothing.** The list holds this hook mounted whether or not a person is
 * open, so *"nobody is selected"* has to be an ordinary state rather than a reason not to call it.
 */

export interface PersonDetailData {
  readonly person: Person | null;
  readonly projects: readonly Project[];
  /**
   * ⚠️⚠️ 31 Oct 2026 · **NO LONGER EVERY TASK AND EVERY HOUR THEY HAVE.** A person's hours run past
   * a thousand rows on UAT and keep growing, so the page's two lists ask the server for one page at
   * a time themselves (`person-screen.tsx`). What this reads instead is bounded: the counts, the
   * menus' facets, and the one month the hours grid draws.
   */
  readonly taskCounts: { readonly total: number; readonly open: number } | null;
  readonly taskFacets: TaskFacets | null;
  readonly logFacets: WorklogFacets | null;
  readonly recentMonth: { readonly month: string; readonly worklogs: readonly Worklog[] } | null;
  /** Moves on every reload, so the page's server-paged lists re-read the page they are on. */
  readonly nonce: number;
  readonly metafields: readonly Metafield[];
  readonly colleagues: readonly Person[];
  readonly teams: readonly Team[];
  readonly identities: readonly IdentityRow[];
  /** C45 · the schedule in force today, or `null` if they have none yet. */
  readonly schedule: PersonSchedule | null;
  /**
   * C51 · the rate in force today, or `null` — which is both *no card* and *not allowed to see
   * one*. ⚠️ **The screen cannot tell those apart, and does not need to**: the DÍJ card is drawn
   * only for a reader with `settings.view`, so a null there always means the person has no rate.
   */
  readonly rate: RateAssignment | null;
  /**
   * C54.1 · ⚠️ **This person's skills only.** The vocabulary and the levels are
   * workspace-wide and the route already holds both for the onboarding wizard. Empty here is
   * both *holds none* and *not allowed to see them*, which the panel does not need to tell
   * apart: reading them needs `people.view`, and a reader without it is not on this page.
   */
  readonly skills: readonly MemberSkill[];
  readonly failure: ScreenFailure | null;
  /** Until this person's first answer: skeleton rows in the tabs and no figures. */
  readonly loading: boolean;
  readonly setIdentities: (rows: readonly IdentityRow[]) => void;
  readonly reload: () => Promise<void>;
  /**
   * ⚠️ **The projects this person has a TASK on**, which is what the mockup's `personProjects`
   * does — and not the same as `projectCount`, which counts memberships. Being on a project and
   * having work on it are different facts, and the KPI and the tab answer different questions.
   */
  readonly theirProjects: readonly Project[];
}

export function usePersonDetail(id: string | null): PersonDetailData {
  const session = useSession();
  const [person, setPerson] = useState<Person | null>(null);
  const [projects, setProjects] = useState<readonly Project[]>([]);
  const [taskCounts, setTaskCounts] = useState<{ total: number; open: number } | null>(null);
  const [taskFacets, setTaskFacets] = useState<TaskFacets | null>(null);
  const [logFacets, setLogFacets] = useState<WorklogFacets | null>(null);
  const [recentMonth, setRecentMonth] = useState<{
    month: string;
    worklogs: readonly Worklog[];
  } | null>(null);
  const [nonce, setNonce] = useState(0);
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  const [colleagues, setColleagues] = useState<readonly Person[]>([]);
  /* R-60 · the list Settings → Teams keeps, live. */
  const teams = useTeams();
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  /**
   * C39 cleanup · the workspace's tracker accounts, so this person's own can be set on their page.
   *
   * ⚠️ **The whole workspace's, because the control is a picker**: what it offers is the accounts
   * nobody has claimed, plus whichever one is already theirs. A failure leaves it empty and the box
   * simply does not appear — a workspace with no tracker is the commonest case, not an error.
   */
  const [identities, setIdentities] = useState<readonly IdentityRow[]>([]);
  /* C45 · which work schedule they are on today. ⚠️ Null is a real answer: somebody hired next
     month has none yet, and the row says so rather than showing zero hours. */
  const [schedule, setSchedule] = useState<PersonSchedule | null>(null);
  /* C51 · their díjkártya, resolved to today by the server. */
  const [rate, setRate] = useState<RateAssignment | null>(null);
  /* C54.1 · the skills panel the mockup draws on this page and C54 skipped. */
  const [skills, setSkills] = useState<readonly MemberSkill[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    /* ⚠️ Nobody selected is not a failure — the list mounts this hook either way. */
    if (id === null || id === '') {
      setPerson(null);
      setFailure(null);
      return;
    }

    const response = await session.request(`/people/${id}`).catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = personSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setPerson(parsed.data);
    setNonce((current) => current + 1);

    /**
     * The tabs, concurrently — and **none of them is fatal**. A caller without `project.view`
     * should still see the person rather than a full-page error about a tab they were not looking
     * at; the same rule `task-route.tsx` records for its pickers.
     */
    const [
      projectList,
      taskTotal,
      taskClosed,
      taskFacetRow,
      logFacetRow,
      fieldList,
      peopleList,
      identityList,
      scheduleRow,
      rateRows,
      skillRows,
    ] = await Promise.all([
      requestAll(session, '/projects').catch(() => null),
      /**
       * ⚠️⚠️ 31 Oct 2026 · **COUNTS AND FACETS, NOT EVERY ROW.** These read every task and every
       * hour the person had (`read-all.ts`), which a heavy logger passes the ceiling of in a few
       * years. The KPI is two counts — all their tasks and the closed ones — the menus are the
       * facets, and the lists page themselves.
       */
      session.request(`/tasks/tracked-hours?assigneeMemberId=${id}`).catch(() => null),
      session
        .request(`/tasks/tracked-hours?assigneeMemberId=${id}&statusCategory=closed`)
        .catch(() => null),
      session.request(`/tasks/facets?assigneeMemberId=${id}`).catch(() => null),
      session.request(`/worklogs/facets?memberId=${id}`).catch(() => null),
      session.request('/metafields?entity=person').catch(() => null),
      /* 26 Oct 2026 · the manager picker's list — every page, not the default page of 50, which
         ended the picker at *M* on UAT. */
      requestAll(session, '/people').catch(() => null),
      /* C39 cleanup · ⚠️ Not fatal for the same reason as the rest: a workspace with no tracker
         answers an empty list and the Integration settings box simply never appears. */
      /* ⚠️ Every page. Identities scale with somebody else's Atlassian, not with our workspace,
         so fifty is a low bar to cross — and a missing row here reads as *"they have no Jira
         account"*, which is a different and wrong answer. */
      requestAll(session, '/integrations/identities').catch(() => null),
      /* C45 · ⚠️ Not fatal either, and it carries the READER's date: a schedule is a timeline, so
         *"the current one"* is a question about a day, and the server's day is the wrong one either
         side of midnight in another timezone. */
      session.request(`/people/${id}/schedule?on=${toWireDate(new Date())}`).catch(() => null),
      /**
       * C51 · ⚠️⚠️ **THE WHOLE WORKSPACE'S ASSIGNMENTS, FOR ONE PERSON'S CARD.** There is no
       * per-person rate endpoint, and this is deliberate rather than pending: the list is one row
       * per member and the resolved figures already travel with it, so a second endpoint would
       * duplicate `listAssignments` to save nothing. The mockup's own `rateOf(id)` reads the same
       * list the same way.
       *
       * ⚠️ **A 403 here is ORDINARY.** The read is guarded by `settings.view` because a rate says
       * what a named colleague costs; a reader without it gets a refusal, this stays null, and the
       * card never appears. Not fatal, like every other request in this batch.
       */
      session.request('/workspace/rate-cards/assignments/all').catch(() => null),
      /**
       * C54.1 · ⚠️ **This person's skills, plus the vocabulary and the levels the picker offers.**
       * Three reads rather than one because the last two are workspace-wide and the first is not;
       * they join in the panel, exactly as the account screen joins them.
       *
       * ⚠️ **A refusal is ordinary**, like every other request in this batch: reading somebody
       * else's skills needs `people.view`, and without it the panel is simply empty.
       */
      session.request(`/skills/members/${id}`).catch(() => null),
    ]);

    if (projectList?.ok === true) {
      const list = parseList(projectSchema, await projectList.json());
      if (list !== null) setProjects(list);
    }
    const countOf = async (response: Response | null): Promise<number | null> => {
      if (response?.ok !== true) return null;
      const body = (await response.json().catch(() => null)) as { count?: unknown } | null;
      return typeof body?.count === 'number' ? body.count : null;
    };
    const total = await countOf(taskTotal);
    const closed = await countOf(taskClosed);
    setTaskCounts(total === null || closed === null ? null : { total, open: total - closed });
    if (taskFacetRow?.ok === true) {
      const parsedFacets = taskFacetsSchema.safeParse(await taskFacetRow.json());
      setTaskFacets(parsedFacets.success ? parsedFacets.data : null);
    }
    if (logFacetRow?.ok === true) {
      const parsedFacets = worklogFacetsSchema.safeParse(await logFacetRow.json());
      const facets = parsedFacets.success ? parsedFacets.data : null;
      setLogFacets(facets);
      /**
       * The hours grid's month — their newest with hours — read alone. ⚠️ Every page of ONE month
       * of one person, which the calendar bounds; nothing to read when they have no hours yet.
       */
      const month = facets?.months[0];
      if (month === undefined) {
        setRecentMonth(null);
      } else {
        const last = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
        const rows = await readAll(
          session,
          `/worklogs?memberId=${id}&from=${month}-01&to=${month}-${String(last).padStart(2, '0')}`,
          worklogSchema,
        ).catch(() => null);
        setRecentMonth(rows === null ? null : { month, worklogs: rows });
      }
    }
    if (fieldList?.ok === true) {
      const list = parseList(metafieldSchema, await fieldList.json());
      if (list !== null) setMetafields(list);
    }
    if (identityList?.ok === true) {
      const list = parseList(identityRowSchema, await identityList.json());
      if (list !== null) setIdentities(list);
    }
    if (scheduleRow?.ok === true) {
      /* ⚠️ `{ data: … | null }`, so a person with no schedule answers 200 with a null rather than
         a 404 — it is an absence of a row, not a missing person. */
      const body = (await scheduleRow.json()) as { data?: unknown };
      const parsed = personScheduleSchema.safeParse(body.data);
      setSchedule(parsed.success ? parsed.data : null);
    }
    if (rateRows?.ok === true) {
      const parsed = rateAssignmentListSchema.safeParse(await rateRows.json());
      /* ⚠️ The LAST row for this person that has already started — the list is ordered
         `from_date desc` per member, so the first match is the one in force. */
      setRate(
        parsed.success
          ? (parsed.data.assignments.find((row) => row.memberId === id) ?? null)
          : null,
      );
    }
    if (skillRows?.ok === true) {
      const list = parseList(memberSkillSchema, await skillRows.json());
      if (list !== null) setSkills(list);
    }
    if (peopleList?.ok === true) {
      const list = parseList(personSchema, await peopleList.json());
      if (list !== null) setColleagues(list);
    }
  }, [session, id]);

  /* First load draws skeleton rows — `use-first-load.ts`, per person: opening somebody else must
     not show the previous person's tabs as this one's, nor an empty tab while theirs arrive. */
  const [settledFor, setSettledFor] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    void reload().finally(() => {
      setSettledFor(id);
    });
  }, [reload, id]);
  const loading = settledFor !== id;

  /**
   * ⚠️ **The project tab shows the projects this person has a TASK on**, which is what the mockup's
   * `personProjects` does — and it is not the same as `projectCount`, which counts memberships.
   * Being on a project and having work on it are different facts, and the KPI and the tab are
   * deliberately answering different questions. The ids come from their tasks' facets (31 Oct
   * 2026), not from holding every task.
   */
  const theirProjects = useMemo(() => {
    const ids = new Set((taskFacets?.projects ?? []).map((one) => one.id));
    return projects.filter((project) => ids.has(project.id));
  }, [projects, taskFacets]);

  return {
    person,
    projects,
    taskCounts,
    taskFacets,
    logFacets,
    recentMonth,
    nonce,
    metafields,
    colleagues,
    teams,
    identities,
    schedule,
    rate,
    skills,
    failure,
    loading,
    setIdentities,
    reload,
    theirProjects,
  };
}
